import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight, Calendar as CalendarIcon, CurrencyDollar, Bookmark, Lightning } from "@phosphor-icons/react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const GrantCalendar = () => {
  const navigate = useNavigate();
  const [grants, setGrants] = useState([]);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [grantsRes, bookmarksRes] = await Promise.all([
          axios.get(`${API}/grants?limit=200`),
          axios.get(`${API}/bookmarks`)
        ]);
        setGrants(grantsRes.data.grants || []);
        const bIds = new Set((bookmarksRes.data.bookmarks || []).map(b => b.grant_id));
        setBookmarkedIds(bIds);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevDays = new Date(year, month, 0).getDate();

    const days = [];
    // Previous month filler
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevDays - i, currentMonth: false, date: null });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, currentMonth: true, date: dateStr });
    }
    // Next month filler
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, currentMonth: false, date: null });
    }
    return days;
  }, [year, month]);

  // Map deadlines to dates
  const grantsByDate = useMemo(() => {
    const map = {};
    for (const g of grants) {
      const d = g.deadline;
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(g);
    }
    return map;
  }, [grants]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const todayStr = new Date().toISOString().slice(0, 10);

  const selectedGrants = selectedDay ? (grantsByDate[selectedDay] || []) : [];

  const getUrgencyClass = (dateStr) => {
    if (!dateStr) return "";
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "opacity-40";
    if (diff <= 1) return "";
    if (diff <= 3) return "";
    return "";
  };

  const getDotColor = (dateStr) => {
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "bg-zinc-600";
    if (diff <= 1) return "bg-red-500";
    if (diff <= 3) return "bg-orange-500";
    if (diff <= 7) return "bg-yellow-500";
    return "bg-emerald-500";
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="grant-calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Grant Calendar</h1>
            <p className="text-zinc-400 mt-1">Visualize upcoming grant deadlines</p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />1 day</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" />3 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" />7 days</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />8+ days</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <Card className="bg-zinc-950 border-zinc-800">
              <CardContent className="p-4 sm:p-6">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-6">
                  <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month-btn">
                    <CaretLeft className="w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold" data-testid="calendar-month-label">{MONTHS[month]} {year}</h2>
                    <Button variant="outline" size="sm" onClick={goToday} className="text-xs border-zinc-700" data-testid="today-btn">
                      Today
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month-btn">
                    <CaretRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 mb-2">
                  {DAYS.map(d => (
                    <div key={d} className="text-center text-xs font-medium text-zinc-500 py-2">{d}</div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-px bg-zinc-800/50 rounded-sm overflow-hidden">
                  {calendarDays.map((cell, i) => {
                    const grantsOnDay = cell.date ? (grantsByDate[cell.date] || []) : [];
                    const isToday = cell.date === todayStr;
                    const isSelected = cell.date === selectedDay;
                    const hasBookmarked = grantsOnDay.some(g => bookmarkedIds.has(g.grant_id));

                    return (
                      <button
                        key={i}
                        onClick={() => cell.date && grantsOnDay.length > 0 && setSelectedDay(cell.date)}
                        className={`min-h-[80px] sm:min-h-[100px] p-1.5 sm:p-2 text-left transition-colors relative ${
                          cell.currentMonth ? "bg-zinc-950" : "bg-zinc-950/50"
                        } ${isSelected ? "ring-2 ring-emerald-500 z-10" : ""} ${
                          grantsOnDay.length > 0 ? "cursor-pointer hover:bg-zinc-900" : "cursor-default"
                        } ${getUrgencyClass(cell.date)}`}
                        data-testid={cell.date ? `calendar-day-${cell.date}` : undefined}
                      >
                        <span className={`text-sm font-medium ${
                          !cell.currentMonth ? "text-zinc-700" : 
                          isToday ? "bg-emerald-500 text-black w-6 h-6 rounded-full flex items-center justify-center" : 
                          "text-zinc-300"
                        }`}>
                          {cell.day}
                        </span>
                        
                        {grantsOnDay.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {grantsOnDay.slice(0, 3).map((g) => (
                              <div 
                                key={g.grant_id} 
                                className={`flex items-center gap-1 rounded-sm px-1 py-0.5 text-[10px] sm:text-xs truncate ${
                                  bookmarkedIds.has(g.grant_id) ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-400"
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getDotColor(cell.date)}`} />
                                <span className="truncate">{g.title.split(" ").slice(0, 3).join(" ")}</span>
                              </div>
                            ))}
                            {grantsOnDay.length > 3 && (
                              <span className="text-[10px] text-zinc-500 pl-1">+{grantsOnDay.length - 3} more</span>
                            )}
                          </div>
                        )}

                        {hasBookmarked && (
                          <Bookmark weight="fill" className="absolute top-1 right-1 w-3 h-3 text-emerald-500" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar: Selected Day Detail */}
          <div className="lg:col-span-1">
            <Card className="bg-zinc-950 border-zinc-800 sticky top-20">
              <CardContent className="p-6">
                {selectedDay ? (
                  <>
                    <h3 className="font-bold text-lg mb-1" data-testid="selected-day-label">
                      {new Date(selectedDay + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                    </h3>
                    <p className="text-sm text-zinc-500 mb-4">{selectedGrants.length} grant{selectedGrants.length !== 1 ? "s" : ""} due</p>
                    
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {selectedGrants.map(g => {
                        const diff = Math.ceil((new Date(g.deadline) - new Date()) / (1000 * 60 * 60 * 24));
                        return (
                          <button
                            key={g.grant_id}
                            onClick={() => navigate(`/grants/${g.grant_id}`)}
                            className="w-full text-left p-3 rounded-sm border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 transition-colors"
                            data-testid={`calendar-grant-${g.grant_id}`}
                          >
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className="text-xs px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-400">{g.category}</span>
                              {bookmarkedIds.has(g.grant_id) && (
                                <Bookmark weight="fill" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm font-medium mt-1">{g.title}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="flex items-center gap-1 text-xs text-emerald-400">
                                <CurrencyDollar className="w-3.5 h-3.5" />
                                ${g.amount_min?.toLocaleString()} - ${g.amount_max?.toLocaleString()}
                              </span>
                              <span className={`text-xs font-semibold ${
                                diff < 0 ? "text-red-500" : diff <= 1 ? "text-red-400" : diff <= 3 ? "text-orange-400" : diff <= 7 ? "text-yellow-400" : "text-zinc-400"
                              }`}>
                                {diff < 0 ? "Expired" : diff <= 1 ? "Tomorrow" : `${diff} days`}
                              </span>
                            </div>
                            {g.match_score != null && (
                              <div className="mt-2">
                                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${
                                  g.match_score >= 70 ? "bg-emerald-500/20 text-emerald-400" :
                                  g.match_score >= 40 ? "bg-amber-500/20 text-amber-400" :
                                  "bg-zinc-800 text-zinc-400"
                                }`}>
                                  {g.match_score}% Match
                                </span>
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">Click a day with deadlines to see grant details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default GrantCalendar;
